import { Form, Formik, FormikErrors, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import acceptedJobApi from '../../../APIs/jobs';
import { ReactComponent as CloseSVG } from '../../../assets/icons/cross.svg';
import {
  BrowseFile,
  Button,
  ErrorMessage,
  FormContainer,
  Input,
  ListImage,
  TextArea,
  Typography,
} from '../../../designComponents';
import { RootState } from '../../../store/selectors';
import {
  FileWithKeys,
  getFileURL,
  requestAPI,
  uploadFilesToS3,
} from '../../../utils';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';
import {
  IndividualSelectedPDFWrapperStyled,
  RemoveAdditionalNotesStyled,
  UploadAndSendToReviewContainerStyled,
} from './style';
import UploadedPDFView from './UploadedPDFView';

type IndividualFileType = {
  file: File;
  title: string;
  id: string;
};

type InitialStateType = {
  pdf: IndividualFileType[];
  pdfNotes: string[];
  jobAndProposalId: string;
};

type InitialStatesDocumentType = {
  pdf: IndividualFileType[];
  pdfNotes: string[];
  jobAndProposalId: string;
};

const validationSchema = yup.object({
  pdf: yup
    .array()
    .of(
      yup.object({
        title: yup.string().required('Title is required.'),
        file: yup.mixed().required('File is required.'),
      })
    )
    .min(1, 'At least one pdf is required.'),

  pdfNotes: yup
    .array()
    .of(yup.string().required('Notes is required.'))
    .min(1, 'At least one note is required.'),
});

type Props = {
  jobAndProposalId: string;
};

const UploadAndSendToReview = ({ jobAndProposalId }: Props) => {
  const formikRef = useRef<FormikProps<InitialStateType>>(null);
  const [loading, setLoading] = useState(false);
  const [isSendingForReview, setIsSendingForReview] = useState(false);

  const [modalStatus, setModalStatus] = useState(false);

  const [files, setFiles] = useState<File[]>([]);
  const [initialValues, _setInitialValues] = useState<InitialStateType>({
    pdf: [],
    pdfNotes: [''],
    jobAndProposalId: jobAndProposalId,
  });
  const jobProcess = useSelector((state: RootState) => state.singleJobProcess);
  const reviews = jobProcess?.review || [];
  const latestReview = reviews.reduce(
    (
      latest: (typeof jobProcess)['review'][0] | null,
      current: (typeof jobProcess)['review'][0]
    ) => {
      if (
        !latest ||
        new Date(current.reviewSendDate) > new Date(latest.reviewSendDate)
      ) {
        return current;
      }
      return latest;
    },
    null
  );

  const handleAddFiles = (selectedFiles: File[]) => {
    if (formikRef?.current) {
      const existedPDFs = [...formikRef.current.values.pdf];
      const addedPDFs = [
        ...existedPDFs,
        { id: `${existedPDFs.length + 1}`, file: selectedFiles[0], title: '' },
      ];
      setFiles(addedPDFs?.map((file) => file.file));
      formikRef.current.setFieldValue('pdf', addedPDFs);
    }
  };

  const handleRemoveFiles = (index: number) => {
    if (formikRef?.current) {
      const existedPDFs = [...formikRef.current.values.pdf];

      existedPDFs.splice(index, 1);
      formikRef.current.setFieldValue('pdf', existedPDFs);
    }
  };

  const handleAddNotes = () => {
    if (formikRef?.current) {
      const existedNotes = [...formikRef.current.values.pdfNotes];
      const addedNotes = [...existedNotes, ''];

      formikRef.current.setFieldValue('pdfNotes', addedNotes);
    }
  };

  const handleRemoveNotes = (index: number) => {
    if (formikRef?.current) {
      const existedNotes = [...formikRef.current.values.pdfNotes];
      if (existedNotes.length > 1) {
        existedNotes.splice(index, 1);
        formikRef.current.setFieldValue('notes', existedNotes);
      }
    }
  };

  const uploadDocument = async (values: InitialStatesDocumentType) => {
    try {
      if (files.length === 0) return;
      setLoading(true);

      let filesWithKey: FileWithKeys[] = [];

      if (files && files.length > 0) {
        filesWithKey = (await uploadFilesToS3(files)) as FileWithKeys[];
      }

      if (filesWithKey && filesWithKey.length === files.length) {
        await requestAPI(
          acceptedJobApi.sendPdf({
            jobAndProposalId: values.jobAndProposalId,
            pdfNotes: values.pdfNotes,
            pdf: values.pdf.map((data) => {
              return {
                title: data.title,
                url: getFileURL(filesWithKey, data.file),
              };
            }),
          })
        );
      }
      await requestAPI(
        acceptedJobApi.getJobProcessByJobProposal(jobAndProposalId)
      );

      setLoading(false);
      toast.success('Document uploaded successfully');
    } catch (error) {
      console.log('Error :', error);
    }
  };

  const handleSendForReview = async () => {
    setIsSendingForReview(true);
    try {
      if (!latestReview) {
        setIsSendingForReview(false);
        return;
      }
      const jobProcessId = jobProcess._id;
      const reviewId = latestReview._id;
      if (!jobProcessId || !reviewId) {
        setIsSendingForReview(false);
        return;
      }
      await requestAPI(acceptedJobApi.sendForReview(jobProcessId, reviewId));
      toast.success('Review send successfully');
      await requestAPI(
        acceptedJobApi.getJobProcessByJobProposal(jobAndProposalId)
      );

      setIsSendingForReview(false);
    } catch (error) {
      console.log('Error sending for review:', error);
      setIsSendingForReview(false);
    }
  };

  const onCloseModal = () => setModalStatus(false);

  const onSentForReview = () => setModalStatus(true);

  return (
    <UploadAndSendToReviewContainerStyled>
      {(reviews.length === 0 ||
        (latestReview && latestReview.reviewStatus !== 'PENDING')) && (
        <>
          <Typography>Please upload the complete PDF </Typography>
          <Typography fontColor="border" style={{ margin: '0px 0px 20px ' }}>
            Please choose a file or you can choose multiple PDFs.
          </Typography>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            onSubmit={uploadDocument}
            validationSchema={validationSchema}
          >
            {({ values, setFieldValue, errors, touched }) => {
              return (
                <Form>
                  <div style={{ marginBottom: '30px' }}>
                    {Array.isArray(values.pdf) &&
                      values.pdf.map((file, idx) => (
                        <IndividualSelectedPDFWrapperStyled
                          key={`${file.id}-${idx}`}
                        >
                          <ListImage
                            files={[file.file]}
                            setFiles={setFiles}
                            onRemoveFile={() => handleRemoveFiles(idx)}
                            noWrapper
                            imageWidth="100px"
                          />
                          <FormContainer
                            label={`PDF Title ${
                              values.pdf.length > 1 ? `( ${idx + 1})` : ''
                            }`}
                            error={
                              touched?.pdf && touched?.pdf[idx]?.title
                                ? errors?.pdf &&
                                  (
                                    errors?.pdf[idx] as FormikErrors<{
                                      title: string;
                                      file: string;
                                    }>
                                  )?.title
                                : ''
                            }
                          >
                            <Input
                              placeholder="Please enter title"
                              onChange={(e) => {
                                const { value } = e.target;
                                const updatedPdf = [...values.pdf];
                                updatedPdf[idx].title = value;
                                setFieldValue('pdf', updatedPdf);
                              }}
                            />
                          </FormContainer>
                        </IndividualSelectedPDFWrapperStyled>
                      ))}

                    <BrowseFile
                      files={[]}
                      setFiles={handleAddFiles}
                      accepts=".pdf"
                      isUploading={false}
                      showSelectFiles={false}
                    />
                    {typeof errors.pdf === 'string' &&
                    touched.pdf &&
                    errors.pdf ? (
                      <ErrorMessage text={errors.pdf} />
                    ) : (
                      ''
                    )}
                    {Array.isArray(values?.pdfNotes) &&
                      values?.pdfNotes.map((note, idx) => (
                        <div key={`${idx}`}>
                          {values?.pdfNotes?.length > 1 && (
                            <RemoveAdditionalNotesStyled>
                              <CloseSVG
                                onClick={() => handleRemoveNotes(idx)}
                              />
                            </RemoveAdditionalNotesStyled>
                          )}
                          <FormContainer
                            label={`Add Note  ${
                              values.pdfNotes.length > 1 ? `( ${idx + 1})` : ''
                            }`}
                            error={
                              touched?.pdfNotes
                                ? errors?.pdfNotes && errors?.pdfNotes[idx]
                                : ''
                            }
                          >
                            <TextArea
                              onChange={(e) => {
                                const { value } = e.target;
                                const existedNotes = [...values.pdfNotes];
                                existedNotes[idx] = value;
                                setFieldValue('pdfNotes', existedNotes);
                              }}
                              value={note}
                              placeholder="Please add notes."
                              showCount
                              maxLength={800}
                            />
                          </FormContainer>
                        </div>
                      ))}
                    <div style={{ marginBottom: '30px' }}>
                      {typeof errors.pdfNotes === 'string' && (
                        <ErrorMessage text={errors.pdfNotes} />
                      )}
                      <Button
                        onClick={handleAddNotes}
                        variant="gray"
                        size="xs"
                        width="112px"
                      >
                        Add More notes
                      </Button>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                      }}
                    >
                      <Button
                        variant="secondary"
                        type="submit"
                        isLoading={loading}
                      >
                        Upload document
                      </Button>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </>
      )}
      {latestReview && latestReview.reviewStatus === 'PENDING' && (
        <>
          <Typography
            variant="title"
            strong
            fontColor="secondary"
            style={{ marginBottom: '20px' }}
          >
            LATEST PDF
          </Typography>
          <UploadedPDFView review={latestReview} />
          <Button
            onClick={onSentForReview}
            isLoading={isSendingForReview}
            style={{ margin: '20px 0px 0px' }}
          >
            Send For Review
          </Button>
        </>
      )}
      <ConfirmationModal
        isOpen={modalStatus}
        onClose={onCloseModal}
        title="Review"
        message="Are you sure you want to send these pdf's to Escape Plan for review?"
        onOk={handleSendForReview}
        okText="Send"
        loading={isSendingForReview}
      />
    </UploadAndSendToReviewContainerStyled>
  );
};

export default UploadAndSendToReview;
